'use client';

import { useState, useEffect } from 'react';
import TestimonialsTab from './TestimonialsTab';
import VideoTab from './VideoTab';

interface Testimonial {
  id: string;
  author: string;
  source: string;
  text: string;
  rating: number;
}

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  embedUrl: string;
}

interface TestimonialsPageProps {
	data?: {
		heading?: {
			title?: string
		}
		content_collection?: {
			id?: string
			reviews_id?: {
				rating?: number
				author?: string
				comment?: string
			}
		}[]
		content_collection_video?: {
			id: string
			site_content_collections_id: {
				collection: {
					item: {
						title?: string | null
						text?: string | null
						embed_url?: string | null
					}
				}[]
			}
		}[]
	}
}

export const fields = [
	'heading.title',
	'content_collection.id',
	'content_collection.reviews_id.rating',
	'content_collection.reviews_id.author',
	'content_collection.reviews_id.comment',

	'content_collection_video.id',
	'content_collection_video.site_content_collections_id.collection.item:site_content.title',
	'content_collection_video.site_content_collections_id.collection.item:site_content.text',
	'content_collection_video.site_content_collections_id.collection.item:videos.embed_url',
] as const

// Function to check if URL is a YouTube link
const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

// Function to extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  
  return (match && match[2].length === 11)
    ? match[2]
    : null
}

// Function to create YouTube embed URL
const getYouTubeEmbedUrl = (videoId: string): string => {
  return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1&autoplay=1`
}

// Function to process video URL and return proper embed URL
const processVideoUrl = (url: string): string => {
  if (!url) return ''
  
  if (isYouTubeUrl(url)) {
    const videoId = getYouTubeVideoId(url)
    return videoId ? getYouTubeEmbedUrl(videoId) : url
  }
  
  return url // Return as-is for non-YouTube URLs
}

export default function TestimonialsPage({ data }: TestimonialsPageProps) {
	// Tab state
	const [activeTab, setActiveTab] = useState<'testimonials' | 'video'>('testimonials');

	// Check URL hash on component mount and set active tab accordingly
	useEffect(() => {
		const checkHash = () => {
			const hash = window.location.hash;
			if (hash === '#video') {
				setActiveTab('video');
			}
		};

		// Check hash on mount
		checkHash();

		// Listen for hash changes
		window.addEventListener('hashchange', checkHash);

		// Cleanup
		return () => {
			window.removeEventListener('hashchange', checkHash);
		};
	}, []);

	// Update URL hash when tab changes
	const handleTabChange = (tab: 'testimonials' | 'video') => {
		setActiveTab(tab);
		
		// Update URL hash without page reload
		if (tab === 'video') {
			window.history.pushState(null, '', '#video');
		} else {
			// Remove hash for testimonials tab
			window.history.pushState(null, '', window.location.pathname + window.location.search);
		}
	};

	// Transform testimonial data from props
	const testimonials: Testimonial[] = data?.content_collection?.map((item) => ({
		id: item?.id || '',
		author: item?.reviews_id?.author || '',
		source: 'Google',
		text: item?.reviews_id?.comment || '',
		rating: item?.reviews_id?.rating || 5
	})) || [];

	// Extract video data from Directus structure
	const videos: Video[] = data?.content_collection_video?.map((videoCollection, index) => {
		const title = videoCollection.site_content_collections_id.collection.find(item => item.item.title)?.item.title || '';
		const rawEmbedUrl = videoCollection.site_content_collections_id.collection.find(item => item.item.embed_url)?.item.embed_url || '';
		
		return {
			id: videoCollection.id,
			title: title,
			thumbnail: '/api/placeholder/400/225',
			embedUrl: processVideoUrl(rawEmbedUrl)
		};
	}).filter(video => video.embedUrl) || []; // Only include videos with embed URLs

	return (
		<section className='TestimonialsPage bg-[#e8e8e8] py-16'>
			<div className='container'>
				<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-4 max-md:items-center'>
					{data?.heading?.title && <h2>{data.heading.title}</h2>}

					{/* Tab buttons */}
					<div className='flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm'>
						<button
							onClick={() => handleTabChange('testimonials')}
							className={`px-6 py-2 rounded-md text-lg font-medium transition-all uppercase ${
								activeTab === 'testimonials'
									? 'bg-navy text-white shadow-sm'
									: 'text-navy hover:bg-gray-50'
							}`}
						>
							Reviews
						</button>
						<button
							onClick={() => handleTabChange('video')}
							className={`px-6 py-2 rounded-md text-lg font-medium transition-all ${
								activeTab === 'video'
									? 'bg-navy text-white shadow-sm'
									: 'text-navy hover:bg-gray-50'
							}`}
						>
							VIDEO
						</button>
					</div>
				</div>

				{/* Tab Content */}
				{activeTab === 'testimonials' && (
					<TestimonialsTab testimonials={testimonials} />
				)}
				{activeTab === 'video' && <VideoTab videos={videos} />}
			</div>
		</section>
	)
}